import BaseBubbleController from "./BaseBubbleController.js";
import {
  escapeBubbleHtml,
  formatBubbleDuration,
  formatBubbleTimestamp,
  renderBubbleFooter,
  renderMarkdownContent,
} from "./bubbleControllerHtml.js";

export default class AssistantThinkingBubbleController extends BaseBubbleController {
  constructor() {
    super("assistant-thinking", "assistant");
    this.durationIntervals = new Map();
    this.durationStateByBubbleId = new Map();
  }

  onBubbleInit(bubble, uiApi) {
    this.stopDurationUpdates(bubble?.bubbleId);
    if (!bubble?.bubbleId || !bubble?.isDurationActive) {
      return;
    }

    this.durationStateByBubbleId.set(bubble.bubbleId, {
      bubbleId: bubble.bubbleId,
      startTimestamp: bubble.startTimestamp,
      endTimestamp: bubble.endTimestamp,
      durationDisplayMode: bubble.durationDisplayMode,
      // Keep durationActive until we detect an end timestamp or isDurationActive flip.
      isDurationActive: bubble.isDurationActive,
    });

    const updateDuration = () => {
      const state = this.durationStateByBubbleId.get(bubble.bubbleId);
      if (!state) {
        this.stopDurationUpdates(bubble.bubbleId);
        return;
      }

      // If we already know the final end time, stop counting immediately.
      if (state.endTimestamp instanceof Date && !isNaN(state.endTimestamp.getTime())) {
        this.stopDurationUpdates(bubble.bubbleId);
        this.paintDurationText(
          uiApi,
          formatBubbleDuration(
            state.startTimestamp,
            state.endTimestamp,
            state.durationDisplayMode
          )
        );
        return;
      }

      const durationNowOrEnd = state.isDurationActive ? new Date() : state.endTimestamp;
      this.paintDurationText(
        uiApi,
        formatBubbleDuration(
          state.startTimestamp,
          durationNowOrEnd,
          state.durationDisplayMode
        )
      );
    };

    updateDuration(); // immediate paint before interval tick
    const intervalId = window.setInterval(updateDuration, 100);
    this.durationIntervals.set(bubble.bubbleId, intervalId);
  }

  onBubbleCompleted(bubble, uiApi) {
    this.stopDurationUpdates(bubble?.bubbleId);
    if (bubble?.bubbleId) {
      this.durationStateByBubbleId.set(bubble.bubbleId, {
        bubbleId: bubble.bubbleId,
        startTimestamp: bubble.startTimestamp,
        endTimestamp: bubble.endTimestamp,
        durationDisplayMode: bubble.durationDisplayMode,
        isDurationActive: bubble.isDurationActive,
      });
    }
    this.paintDurationText(
      uiApi,
      formatBubbleDuration(
        bubble?.startTimestamp,
        bubble?.endTimestamp,
        bubble?.durationDisplayMode
      )
    );
  }

  onBubbleUpdated(bubble, uiApi) {
    if (bubble?.bubbleId) {
      this.durationStateByBubbleId.set(bubble.bubbleId, {
        bubbleId: bubble.bubbleId,
        startTimestamp: bubble.startTimestamp,
        endTimestamp: bubble.endTimestamp,
        durationDisplayMode: bubble.durationDisplayMode,
        isDurationActive: bubble.isDurationActive,
      });
    }

    if (bubble?.isDurationActive) {
      if (!this.durationIntervals.has(bubble?.bubbleId)) {
        this.onBubbleInit(bubble, uiApi);
      }
      this.syncStreamingState(bubble, uiApi);
      return;
    }
    this.syncStreamingState(bubble, uiApi);
    this.onBubbleCompleted(bubble, uiApi);
  }

  onBubbleRemoved(bubble) {
    this.stopDurationUpdates(bubble?.bubbleId);
    if (bubble?.bubbleId) {
      this.durationStateByBubbleId.delete(bubble.bubbleId);
    }
  }

  stopDurationUpdates(bubbleId) {
    const intervalId = this.durationIntervals.get(bubbleId);
    if (intervalId) {
      window.clearInterval(intervalId);
      this.durationIntervals.delete(bubbleId);
    }
  }

  renderBubbleHtml(bubble) {
    const isExpanded = bubble.isExpanded !== false;
    const durationText = formatBubbleDuration(
      bubble.startTimestamp,
      bubble.isDurationActive ? new Date() : bubble.endTimestamp,
      bubble.durationDisplayMode
    );
    const isComplete = bubble?.isComplete === true;
    const bodyContentHtml = isComplete
      ? renderMarkdownContent(bubble.content || "")
      : `<div class="bubble-controller-streaming-text" data-bubble-thinking-streaming-text="true">${escapeBubbleHtml(
          bubble.content || ""
        )}</div>`;
    const bodyHtml = `
      <div
        class="bubble-controller-thinking-body"
        data-bubble-thinking-body="true"
        ${isExpanded ? "" : 'hidden=""'}
      >
        ${bodyContentHtml}
      </div>
    `.trim();
    const panelHtml = `
      <div class="bubble-controller-panel bubble-controller-panel-assistant bubble-controller-panel-thinking" data-bubble-context-menu-target="true">
        <button class="bubble-controller-thinking-header" type="button">
          <span class="bubble-controller-thinking-title">Thinking</span>
          <span class="bubble-controller-thinking-header-right">
            <span class="bubble-controller-thinking-duration" data-bubble-duration="true">${escapeBubbleHtml(
              durationText
            )}</span>
            <span class="bubble-controller-thinking-chevron" data-bubble-thinking-chevron="true">${
              isExpanded ? "Hide" : "Show"
            }</span>
          </span>
        </button>
        ${bodyHtml}
      </div>
    `.trim();

    const footerHtml = renderBubbleFooter({
      role: "assistant",
      timestamp: bubble.showTimestamp
        ? formatBubbleTimestamp(bubble.timestamp)
        : "",
    });

    return [panelHtml, footerHtml].filter(Boolean).join("");
  }

  onBubbleRendered(bubble, uiApi) {
    const header = uiApi?.query?.(".bubble-controller-thinking-header");
    if (header && !header.dataset.controllerBound) {
      header.dataset.controllerBound = "true";
      header.addEventListener("click", () => {
        uiApi?.updateBubble?.(bubble.bubbleId, {
          isExpanded: bubble.isExpanded === false,
        });
      });
    }

    this.paintDurationText(
      uiApi,
      formatBubbleDuration(
        bubble?.startTimestamp,
        bubble?.isDurationActive ? new Date() : bubble?.endTimestamp,
        bubble?.durationDisplayMode
      )
    );
    this.syncStreamingState(bubble, uiApi);
  }

  paintDurationText(uiApi, text) {
    const durationElement = uiApi?.query?.("[data-bubble-duration]");
    if (durationElement) {
      durationElement.textContent = text || "";
    }
  }

  syncStreamingState(bubble, uiApi) {
    const thinkingBody = uiApi?.query?.("[data-bubble-thinking-body]");
    if (thinkingBody) {
      thinkingBody.hidden = bubble?.isExpanded === false;
    }

    const chevron = uiApi?.query?.("[data-bubble-thinking-chevron]");
    if (chevron) {
      chevron.textContent = bubble?.isExpanded === false ? "Show" : "Hide";
    }

    const textElement = uiApi?.query?.("[data-bubble-thinking-streaming-text]");
    if (textElement) {
      textElement.textContent = bubble?.content || "";
    }

    const timestampElement = uiApi?.query?.(".bubble-controller-timestamp");
    if (timestampElement && bubble?.showTimestamp) {
      timestampElement.textContent = formatBubbleTimestamp(bubble?.timestamp) || "";
    }
  }
}
